import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { remoteCallService } from '@/services';

export const remoteCallQueryKeys = {
  all: ['remoteCalls'],
  slotsByInterviewer: (interviewerId, page, perPage) => [...remoteCallQueryKeys.all, 'slotsByInterviewer', interviewerId, page, perPage],
  meetingLinkStatus: () => [...remoteCallQueryKeys.all, 'meetingLinkStatus'],
};

// Hook to get remote call slots by interviewer
export const useSlotsByInterviewer = (interviewerId, page = 1, perPage = 10, options = {}) => {
  return useQuery({
    queryKey: remoteCallQueryKeys.slotsByInterviewer(interviewerId, page, perPage),
    queryFn: async () => {
      try {
        return await remoteCallService.getSlotsByInterviewer(interviewerId, page, perPage);
      } catch (error) {
        // Handle 404 errors specifically - when no slots are found
        if (error.response?.status === 404) {
          console.log('No slots found for interviewer, returning empty data structure');
          // Return empty data structure that matches expected format
          return {
            slots: {
              data: [],
              current_page: page,
              last_page: 1,
              per_page: perPage,
              total: 0
            }
          };
        }
        // Re-throw other errors to maintain error handling for genuine issues
        throw error;
      }
    },
    enabled: !!interviewerId, // Only run query if interviewerId is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

// Hook to get meeting link status counts
export const useMeetingLinkStatus = (options = {}) => {
  return useQuery({
    queryKey: remoteCallQueryKeys.meetingLinkStatus(),
    queryFn: () => remoteCallService.getMeetingLinkStatus(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

// Mutation to store meeting link
export const useStoreMeetingLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slotId, meetingLink }) => remoteCallService.storeMeetingLink(slotId, meetingLink),
    onSuccess: () => {
      // Invalidate and refetch both queries
      queryClient.invalidateQueries({ queryKey: remoteCallQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: remoteCallQueryKeys.meetingLinkStatus() });
    },
  });
};